import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { catchError, EMPTY } from 'rxjs';

import { FeedApiService } from 'src/app/api/queries/feed-api.service';
import { ContentFilters, Filter } from '../models/core.models';

import * as feedActions from '../actions/feed.actions';

/**
 * The filter panel's two reads.
 *
 * **Requests only; `FeedState.filters` stays NgRx.** It is not a cached read:
 * most of its writers are local edits of the per-item `checked` / `active` /
 * `type` flags, which are a *projection* of `selectedFilterItems` /
 * `activeFilterItems` that the reducer re-derives on every response through
 * `mergeItemWithState`. Caching the array would mean patching it from nine
 * places to reproduce a merge the reducer already does in one.
 *
 * Each method dispatches the **trigger** before the request, the `…Success`
 * after it, and the `…Fail` on error. The triggers are not vestigial:
 * `loadFeedFilters` clears the list and `loadFilter` raises that filter's
 * spinner - which is why `loadFilterFail` must land, to lower it again.
 */
@Injectable({ providedIn: 'root' })
export class FeedFiltersService {
  private readonly feedApi = inject(FeedApiService);
  private readonly store = inject(Store);

  /** The feed's filter structure. */
  loadFilters(feedId: string): void {
    this.store.dispatch(feedActions.loadFeedFilters({ feedId }));

    this.feedApi
      .getFeedFilters(feedId)
      .pipe(
        catchError((error) => {
          this.store.dispatch(feedActions.loadFeedFiltersFail({ error }));
          return EMPTY;
        }),
      )
      .subscribe((response) =>
        this.store.dispatch(
          feedActions.loadFeedFiltersSuccess({ filters: response.available }),
        ),
      );
  }

  /**
   * One page of a single filter's items.
   *
   * `offset > 0` is "show more" and the reducer appends; anything else replaces,
   * which is how a content-filter change re-reads an expanded filter in place.
   */
  loadFilterItems(
    feedId: string,
    filter: Filter,
    contentFilters: ContentFilters,
    offset: number = 0,
  ): void {
    this.store.dispatch(
      feedActions.loadFilter({ feedId, filter, contentFilters, offset }),
    );

    this.feedApi
      .getFeedFilterItems(feedId, filter, contentFilters, offset)
      .pipe(
        catchError((error) => {
          this.store.dispatch(feedActions.loadFilterFail({ filter, error }));
          return EMPTY;
        }),
      )
      .subscribe((filterResponse) =>
        this.store.dispatch(feedActions.loadFilterSuccess({ filterResponse })),
      );
  }
}
